import { Transition } from '@headlessui/react';
import PopupVideo from '@/components/(features)/surveyWizard/layout/PopupVideo';
import { SurveyRendererProps } from '@/components/(features)/surveyWizard/(wizards)/component-1/SurveyRenderer';
import Image from 'next/image';
import React from 'react';
import { ChevronLeft, Hourglass } from 'lucide-react';
import { safeHtmlLinks } from '@/utils/links/safeHref';

interface ExtraStaticStepProps {
    showGroupFieldsBeforeMobile: SurveyRendererProps['showGroupFieldsBeforeMobile'];
    currentStep: number;
    onNextStep: () => void;
    percent?: any;
    timer?: any;
}

const StaticCountdown2: React.FC<{ timer: string }> = ({ timer }) => {
    const [hours, setHours] = React.useState('00');
    const [minutes, setMinutes] = React.useState('00');
    const [seconds, setSeconds] = React.useState('00');

    React.useEffect(() => {
        const parts = timer.split(':').map(Number);
        const h = parts[0] || 0;
        const m = parts[1] || 0;
        const s = parts[2] || 0;
        const targetSeconds = h * 3600 + m * 60 + s;
        const startTime = Date.now();

        const interval = setInterval(() => {
            const now = Date.now();
            const endTime = startTime + targetSeconds * 1000;
            const distance = endTime - now;

            if (distance <= 0) {
                setHours('00');
                setMinutes('00');
                setSeconds('00');
                clearInterval(interval);
            } else {
                const hrs = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const mins = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const secs = Math.floor((distance % (1000 * 60)) / 1000);

                setHours(String(hrs).padStart(2, '0'));
                setMinutes(String(mins).padStart(2, '0'));
                setSeconds(String(secs).padStart(2, '0'));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    return (
        <div className='flex items-center justify-center gap-1.5 text-xs text-gray-400 font-medium select-none mt-4'>
            <Hourglass className='size-4 shrink-0 text-[#EEAC43]' />
            <span>زمان باقی مانده</span>
            <span className='font-bold text-gray-200'>{hours}</span>
            <span>ساعت</span>
            <span className='font-bold text-gray-200'>{minutes}</span>
            <span>دقیقه</span>
            <span className='font-bold text-gray-200'>{seconds}</span>
            <span>ثانیه</span>
        </div>
    );
};

export const ExtraStaticStep2: React.FC<ExtraStaticStepProps> = ({
    showGroupFieldsBeforeMobile,
    currentStep,
    onNextStep,
    percent,
    timer,
}) => {
    if (!showGroupFieldsBeforeMobile) return null;
    return (
        <Transition
            show={currentStep === 0}
            enter='transition-all transform duration-500 linear'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-all transform duration-100 linear'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            as='div'
            className={`w-full px-6 flex pt-12 pb-6 flex-col items-center justify-start ${
                currentStep === 0 ? 'relative' : 'absolute inset-x-0 top-0 pointer-events-none'
            }`}
        >
            <div className='flex flex-col text-white overflow-hidden relative items-center space-y-3 md:space-y-5 text-center w-full'>
                {/* Title & Subtitle Group */}
                <div className='flex flex-col w-full items-center space-y-1 md:space-y-2'>
                    {/* Title */}
                    <h2 className='text-lg px-2 lg:px-6 md:text-2xl font-black text-white leading-snug'>
                        {showGroupFieldsBeforeMobile.title}
                    </h2>

                    {/* Text/Subtitle */}
                    <p className='text-[10px] md:text-sm text-[#EEAC43] font-bold leading-relaxed max-w-[90%]'>
                        {showGroupFieldsBeforeMobile.text}
                    </p>
                </div>

                {/* Banner Media Block */}
                {showGroupFieldsBeforeMobile?.bannerType === 'video' && (
                    <div className='w-full aspect-video rounded-2xl overflow-hidden shadow-lg border border-white/10'>
                        <PopupVideo
                            video={showGroupFieldsBeforeMobile.video}
                            embedClassName='w-full h-full'
                        />
                    </div>
                )}
                {showGroupFieldsBeforeMobile?.bannerType === 'image' && (
                    <div className='w-full relative aspect-video rounded-2xl overflow-hidden shadow-lg border border-white/10'>
                        <Image
                            className='w-full h-full object-cover z-[1]'
                            width={1800}
                            height={1012}
                            src={showGroupFieldsBeforeMobile?.image?.sourceUrl || ''}
                            alt='wizard banner'
                        />
                        <Image
                            className='aspect-video absolute bottom-0 inset-x-0 -z-1 object-cover'
                            src='/wizard/graph.png'
                            width={1800}
                            height={1800}
                            alt='dollar'
                        />
                    </div>
                )}

                {showGroupFieldsBeforeMobile?.subtitle && (
                    <div
                        className='text-xs text-gray-400 font-medium -mt-2 text-center leading-relaxed [&_del]:line-through [&_p]:mb-1 [&_br]:block'
                        dangerouslySetInnerHTML={{
                            __html: safeHtmlLinks(showGroupFieldsBeforeMobile.subtitle),
                        }}
                    />
                )}

                {/* Dynamic Capacity Indicator */}
                {percent !== undefined && (
                    <div className='flex items-center gap-1.5 text-xs text-gray-400 font-bold select-none justify-center'>
                        <span className='size-2 rounded-full bg-[#FE4657] animate-pulse' />
                        <span>فقط {percent} نفر از ۲۵ ظرفیت باقیست</span>
                    </div>
                )}

                {/* Action button */}
                <button
                    type='button'
                    className='bg-gradient-to-r from-[#C07808] to-[#EEAC43] hover:brightness-105 w-full text-white py-4 px-6 rounded-xl font-bold transition-all flex items-center justify-center gap-2 relative shadow-lg shadow-[#EEAC43]/10'
                    onClick={onNextStep}
                >
                    <span>{showGroupFieldsBeforeMobile.buttonText}</span>
                    <ChevronLeft className='size-5 absolute right-4' />
                </button>

                {/* Footer Timer */}
                {timer && <StaticCountdown2 timer={timer} />}
            </div>
        </Transition>
    );
};
